// Wave 3.47 — Re-cut & Send-to-Broken dialogs
// =============================================================================
// Shared, material-aware dialogs that drive the two new flows on top of the
// existing Broken Packet primitive (Wave 3.45 Phase 2):
//
//   • RecutFromBrokenDialog  — broken → normal packet
//   • SendToBrokenDialog     — normal → broken packet
//
// Used by Diamond / Colour Stone / Pearl packet detail pages. Mounted on the
// packet that owns the action: the Re-cut dialog is mounted on the broken
// packet detail, while Send-to-Broken is mounted on the normal packet detail.
//
// Backend endpoints by ``material``:
//   diamond     →  POST /api/diamonds/packets/{id}/{recut|send-to-broken}
//   color_stone →  POST /api/color-stones/packets/{id}/{recut|send-to-broken}
//   pearl       →  POST /api/pearls/packets/{id}/{recut|send-to-broken}

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Loader2, Scissors, PackageX, Coins, ArrowRight, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
// Wave 3.49 — portal-wide carat/piece validation (Rule 1 + Rule 2).
import { validateMovementQty, assertStateConsistent } from '@/lib/caratValidation';

const MATERIAL_CONFIG = {
  diamond: {
    label: 'Diamond',
    apiPrefix: '/diamonds',
    unit: 'ct',
  },
  color_stone: {
    label: 'Colour Stone',
    apiPrefix: '/color-stones',
    unit: 'ct',
  },
  pearl: {
    label: 'Pearl',
    apiPrefix: '/pearls',
    unit: 'ct',
  },
};

const fmtMoney = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtCt = (n) => Number(n || 0).toFixed(3) + ' ct';

// =============================================================================
// RecutFromBrokenDialog — broken → normal
// =============================================================================
export function RecutFromBrokenDialog({
  open,
  onOpenChange,
  material,            // 'diamond' | 'color_stone' | 'pearl'
  brokenPacket,        // source broken packet doc (must have id, packet_no, balance_*, current_rate_per_ct, classification_id)
  companyId,
  onCompleted,
}) {
  const cfg = MATERIAL_CONFIG[material];

  const [form, setForm] = useState({
    old_carats: '',
    old_pieces: '',
    new_carats: '',
    new_pieces: '',
    note: '',
  });
  const [targetMode, setTargetMode] = useState('existing'); // 'existing' | 'new'
  const [targetPacketId, setTargetPacketId] = useState('');
  const [newPacket, setNewPacket] = useState({
    classification_id: '',
    valuation_mode: 'running_average',
    fixed_rate_per_ct: '',
    description: '',
  });
  const [normalPackets, setNormalPackets] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset form whenever the dialog opens for a fresh packet.
  useEffect(() => {
    if (open) {
      setForm({ old_carats: '', old_pieces: '', new_carats: '', new_pieces: '', note: '' });
      setTargetMode('existing');
      setTargetPacketId('');
      setSearch('');
      setNewPacket({
        classification_id: brokenPacket?.classification_id || '',
        valuation_mode: 'running_average',
        fixed_rate_per_ct: '',
        description: '',
      });
    }
  }, [open, brokenPacket?.classification_id]);

  // Load eligible normal packets (purchased, not broken) for the destination picker.
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      setLoadingTargets(true);
      try {
        const { data } = await api.get(`${cfg.apiPrefix}/packets`, {
          params: { company_id: companyId, packet_type: 'normal' },
        });
        const list = (Array.isArray(data) ? data : [])
          .filter((p) => p.purchase_status !== 'on_approval' && p.packet_type !== 'broken')
          .sort((a, b) => (a.packet_no || '').localeCompare(b.packet_no || ''));
        if (!cancelled) setNormalPackets(list);
      } catch (e) {
        if (!cancelled) toast.error(extractError(e, 'Failed to load packets'));
      } finally {
        if (!cancelled) setLoadingTargets(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, companyId, cfg.apiPrefix]);

  // Load classifications for the "new packet" path.
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`${cfg.apiPrefix}/classifications`, {
          params: { company_id: companyId },
        });
        if (!cancelled) setClassifications(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open, companyId, cfg.apiPrefix]);

  const oldCt = Number(form.old_carats || 0);
  const oldPcs = Number(form.old_pieces || 0);
  const newCt = Number(form.new_carats || 0);
  const newPcs = Number(form.new_pieces || 0);

  const srcRate = Number(brokenPacket?.current_rate_per_ct || 0);
  const preservedValue = +(oldCt * srcRate).toFixed(2);
  const newRate = newCt > 0 ? +(preservedValue / newCt).toFixed(2) : 0;
  const weightLoss = +(oldCt - newCt).toFixed(3);
  const lossPct = oldCt > 0 ? +(((oldCt - newCt) / oldCt) * 100).toFixed(2) : 0;

  // Eligibility checks (mirror server-side guards for instant feedback).
  const maxCt = Number(brokenPacket?.balance_carats || 0);
  const maxPcs = Number(brokenPacket?.balance_pieces || 0);
  const errors = [];
  // Wave 3.49 Rule 1 — old AND new sides each require ct>0 AND pcs>0.
  const oldR1 = validateMovementQty(oldCt, oldPcs, { context: 'source side of re-cut', packetNo: brokenPacket?.packet_no });
  if (oldR1) errors.push(oldR1);
  if (oldCt > maxCt + 1e-9) errors.push(`Old carats exceed packet balance (${maxCt} ct available)`);
  if (oldPcs > maxPcs) errors.push(`Old pieces exceed packet balance (${maxPcs} pcs available)`);
  const newR1 = validateMovementQty(newCt, newPcs, { context: 'destination side of re-cut' });
  if (newR1) errors.push(newR1);
  if (newCt > oldCt + 1e-9) errors.push('New carats cannot exceed old carats (cuts can only lose weight)');
  // Wave 3.49 Rule 2 — broken source's post-recut state must satisfy
  // ((carats > 0) IFF (pieces > 0)).
  const sourceStateErr = assertStateConsistent(maxCt - oldCt, maxPcs - oldPcs, {
    packetNo: brokenPacket?.packet_no, label: 'broken source packet',
    context: 're-cut', txnCarats: oldCt, txnPieces: oldPcs,
  });
  if (sourceStateErr) errors.push(sourceStateErr);
  if (targetMode === 'existing' && !targetPacketId) errors.push('Pick a destination packet');
  if (targetMode === 'new' && !newPacket.classification_id) errors.push('Pick a classification for the new packet');
  if (targetMode === 'new' && newPacket.valuation_mode === 'fixed') {
    if (!Number(newPacket.fixed_rate_per_ct || 0)) errors.push('Enter a fixed rate per ct for the new packet');
  }
  if (srcRate <= 0) errors.push('Broken packet has no rate set — re-cut would lose value information.');

  const filteredTargets = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return normalPackets;
    return normalPackets.filter((p) => {
      const hay = [p.packet_no, p.classification_shape, p.classification_schema, p.description].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [normalPackets, search]);

  const submit = async () => {
    if (errors.length) return;
    setSubmitting(true);
    try {
      const body = {
        old_carats: oldCt,
        old_pieces: oldPcs,
        new_carats: newCt,
        new_pieces: newPcs,
        target_mode: targetMode,
        note: (form.note || '').trim(),
      };
      if (targetMode === 'existing') {
        body.target_packet_id = targetPacketId;
      } else {
        body.new_packet = {
          classification_id: newPacket.classification_id,
          valuation_mode: newPacket.valuation_mode,
          fixed_rate_per_ct: newPacket.valuation_mode === 'fixed'
            ? Number(newPacket.fixed_rate_per_ct || 0)
            : undefined,
          description: (newPacket.description || '').trim() || undefined,
        };
      }
      const { data } = await api.post(
        `${cfg.apiPrefix}/packets/${brokenPacket.id}/recut`,
        body,
      );
      toast.success(
        `Re-cut posted — ${fmtCt(oldCt)} → ${fmtCt(newCt)} routed to ${data?.target_packet?.packet_no || 'destination'}`,
      );
      onOpenChange(false);
      if (onCompleted) onCompleted(data);
    } catch (e) {
      toast.error(extractError(e, 'Re-cut failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!brokenPacket) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="recut-from-broken-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors size={16} className="text-amber-700" />
            Re-cut from Broken {brokenPacket.packet_no}
          </DialogTitle>
          <DialogDescription>
            Move material out of this broken (salvage) packet. The re-cut piece(s) keep the
            same total inventory value — per-carat rate auto-bumps to reflect the cutting loss.
          </DialogDescription>
        </DialogHeader>

        {/* Header strip — broken packet snapshot */}
        <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs grid grid-cols-3 gap-3" data-testid="recut-broken-snapshot">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-amber-700">Available</div>
            <div className="font-mono text-amber-900">
              {fmtCt(maxCt)} · {maxPcs} pcs
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-amber-700">Avg rate</div>
            <div className="font-mono text-amber-900">{fmtMoney(srcRate)}/ct</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-amber-700">Classification</div>
            <div className="text-amber-900">{brokenPacket.classification_shape || brokenPacket.classification_schema}</div>
          </div>
        </div>

        {/* Old → New inputs */}
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="rounded-md border border-[var(--divider)] p-3 bg-slate-50/40">
            <div className="text-xs font-medium text-slate-700 mb-2">Old (before cut)</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-slate-500">Carats</Label>
                <Input
                  type="number" step="0.001" min="0"
                  value={form.old_carats}
                  onChange={(e) => setForm((p) => ({ ...p, old_carats: e.target.value }))}
                  className="h-9 font-mono tabular-nums"
                  data-testid="recut-old-carats-input"
                />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Pieces</Label>
                <Input
                  type="number" min="0"
                  value={form.old_pieces}
                  onChange={(e) => setForm((p) => ({ ...p, old_pieces: e.target.value }))}
                  className="h-9 font-mono tabular-nums"
                  data-testid="recut-old-pieces-input"
                />
              </div>
            </div>
          </div>
          <div className="rounded-md border border-emerald-200 p-3 bg-emerald-50/40">
            <div className="text-xs font-medium text-emerald-800 mb-2 flex items-center gap-1">
              <ArrowRight size={12} /> New (after cut)
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-emerald-700">Carats</Label>
                <Input
                  type="number" step="0.001" min="0"
                  value={form.new_carats}
                  onChange={(e) => setForm((p) => ({ ...p, new_carats: e.target.value }))}
                  className="h-9 font-mono tabular-nums"
                  data-testid="recut-new-carats-input"
                />
              </div>
              <div>
                <Label className="text-[10px] text-emerald-700">Pieces</Label>
                <Input
                  type="number" min="0"
                  value={form.new_pieces}
                  onChange={(e) => setForm((p) => ({ ...p, new_pieces: e.target.value }))}
                  className="h-9 font-mono tabular-nums"
                  data-testid="recut-new-pieces-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Math preview */}
        {(oldCt > 0 && newCt > 0) && (
          <div className="rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs grid grid-cols-4 gap-3" data-testid="recut-math-preview">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-blue-700">Weight loss</div>
              <div className="font-mono text-blue-900">{fmtCt(weightLoss)} ({lossPct}%)</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-blue-700">Value preserved</div>
              <div className="font-mono text-blue-900" data-testid="recut-preserved-value">{fmtMoney(preservedValue)}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-blue-700">New rate / ct</div>
              <div className="font-mono text-blue-900" data-testid="recut-new-rate">{fmtMoney(newRate)}/ct</div>
            </div>
          </div>
        )}

        {/* Destination — existing vs new */}
        <div className="space-y-2 mt-2">
          <Label className="text-xs text-slate-700">Destination packet</Label>
          <Tabs value={targetMode} onValueChange={setTargetMode}>
            <TabsList className="grid grid-cols-2 w-full max-w-md">
              <TabsTrigger value="existing" data-testid="recut-target-mode-existing">
                Existing packet
              </TabsTrigger>
              <TabsTrigger value="new" data-testid="recut-target-mode-new">
                Create new packet
              </TabsTrigger>
            </TabsList>
            <TabsContent value="existing" className="space-y-2 mt-3">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search packet no, shape…"
                  className="h-9 pl-7"
                  data-testid="recut-target-search-input"
                />
              </div>
              {loadingTargets ? (
                <div className="flex items-center justify-center py-6 text-xs text-slate-500">
                  <Loader2 size={14} className="animate-spin mr-2" /> Loading packets…
                </div>
              ) : filteredTargets.length === 0 ? (
                <div className="text-xs text-slate-500 italic text-center py-4">
                  No purchased normal packets found. Switch to "Create new packet" instead.
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-[var(--divider)] rounded-md" data-testid="recut-target-list">
                  {filteredTargets.slice(0, 100).map((p) => {
                    const sel = p.id === targetPacketId;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setTargetPacketId(p.id)}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors border-b border-[var(--divider)] last:border-b-0 ${sel ? 'bg-blue-50/60' : ''}`}
                        data-testid={`recut-target-option-${p.id}`}
                      >
                        <div className="min-w-0">
                          <div className="font-mono font-medium text-[var(--ink-navy)] truncate">
                            {p.packet_no}
                          </div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {p.classification_shape || p.classification_schema} ·
                            {' '}{fmtCt(p.balance_carats)} ·
                            {' '}{p.balance_pieces} pcs
                          </div>
                        </div>
                        {sel && <Badge className="bg-blue-600 text-white text-[10px]">Selected</Badge>}
                      </button>
                    );
                  })}
                </div>
              )}
            </TabsContent>
            <TabsContent value="new" className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-slate-500">Classification</Label>
                  <SearchableSelect
                    items={classifications}
                    value={newPacket.classification_id}
                    onValueChange={(v) => setNewPacket((p) => ({ ...p, classification_id: v || '' }))}
                    itemToLabel={(c) => `${c.schema_code} · ${c.shape_name}`}
                    searchKeys={['schema_code', 'shape_name']}
                    placeholder={classifications.length === 0 ? 'No classifications' : 'Pick classification'}
                    searchPlaceholder="Search shape / code…"
                    disabled={classifications.length === 0}
                    testId="recut-new-classification-select"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">
                    Re-cut may change the shape — pick the new classification.
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Valuation mode</Label>
                  <Select
                    value={newPacket.valuation_mode}
                    onValueChange={(v) => setNewPacket((p) => ({ ...p, valuation_mode: v }))}
                  >
                    <SelectTrigger className="h-9" data-testid="recut-new-valuation-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="running_average">Running average (recommended)</SelectItem>
                      <SelectItem value="fixed">Fixed rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {newPacket.valuation_mode === 'fixed' && (
                <div>
                  <Label className="text-[11px] text-slate-500">Fixed rate / ct</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={newPacket.fixed_rate_per_ct}
                    onChange={(e) => setNewPacket((p) => ({ ...p, fixed_rate_per_ct: e.target.value }))}
                    className="h-9 font-mono tabular-nums"
                    data-testid="recut-new-fixed-rate-input"
                  />
                </div>
              )}
              <div>
                <Label className="text-[11px] text-slate-500">Description (optional)</Label>
                <Input
                  value={newPacket.description}
                  onChange={(e) => setNewPacket((p) => ({ ...p, description: e.target.value }))}
                  placeholder="e.g. Round → Princess re-cut"
                  className="h-9"
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-2">
          <Label className="text-[11px] text-slate-500">Note (optional)</Label>
          <Textarea
            rows={2}
            value={form.note}
            onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
            placeholder="Audit note — what was cut, who supervised, etc."
          />
        </div>

        {errors.length > 0 && (
          <ul className="text-[11px] text-rose-600 list-disc pl-4 space-y-0.5" data-testid="recut-errors">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            className="luxury-button"
            onClick={submit}
            disabled={submitting || errors.length > 0}
            data-testid="recut-submit-button"
          >
            <Scissors size={13} className="mr-1.5" />
            {submitting ? 'Posting re-cut…' : 'Post re-cut'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// SendToBrokenDialog — normal → broken
// =============================================================================
export function SendToBrokenDialog({
  open,
  onOpenChange,
  material,            // 'diamond' | 'color_stone' | 'pearl'
  sourcePacket,        // normal packet doc (id, packet_no, balance_*, current_rate_per_ct, classification_id)
  companyId,
  onCompleted,
}) {
  const cfg = MATERIAL_CONFIG[material];

  const [form, setForm] = useState({
    old_carats: '',
    old_pieces: '',
    new_carats: '',
    new_pieces: '',
    note: '',
  });
  const [targetMode, setTargetMode] = useState('existing');
  const [targetBrokenPacketId, setTargetBrokenPacketId] = useState('');
  const [newBrokenDescription, setNewBrokenDescription] = useState('');
  const [brokenPackets, setBrokenPackets] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ old_carats: '', old_pieces: '', new_carats: '', new_pieces: '', note: '' });
      setTargetMode('existing');
      setTargetBrokenPacketId('');
      setNewBrokenDescription('');
    }
  }, [open]);

  // Load broken packets of THIS material with matching classification.
  useEffect(() => {
    if (!open || !companyId || !sourcePacket?.classification_id) return;
    let cancelled = false;
    (async () => {
      setLoadingTargets(true);
      try {
        const { data } = await api.get(`${cfg.apiPrefix}/packets`, {
          params: {
            company_id: companyId,
            packet_type: 'broken',
            classification_id: sourcePacket.classification_id,
          },
        });
        const list = (Array.isArray(data) ? data : [])
          .filter((p) => p.packet_type === 'broken')
          .sort((a, b) => (a.packet_no || '').localeCompare(b.packet_no || ''));
        if (!cancelled) setBrokenPackets(list);
        // Default to "new" if there are no existing broken packets to pick.
        if (!cancelled && list.length === 0) setTargetMode('new');
      } catch (e) {
        if (!cancelled) toast.error(extractError(e, 'Failed to load broken packets'));
      } finally {
        if (!cancelled) setLoadingTargets(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, companyId, cfg.apiPrefix, sourcePacket?.classification_id]);

  const oldCt = Number(form.old_carats || 0);
  const oldPcs = Number(form.old_pieces || 0);
  const newCt = Number(form.new_carats || 0);
  const newPcs = Number(form.new_pieces || 0);
  const srcRate = Number(sourcePacket?.current_rate_per_ct || 0);
  const preservedValue = +(oldCt * srcRate).toFixed(2);
  const newRate = newCt > 0 ? +(preservedValue / newCt).toFixed(2) : 0;
  const weightLoss = +(oldCt - newCt).toFixed(3);

  const maxCt = Number(sourcePacket?.balance_carats || 0);
  const maxPcs = Number(sourcePacket?.balance_pieces || 0);
  const errors = [];
  // Wave 3.49 Rule 1 — old AND new sides must each have ct > 0 AND pcs > 0.
  const oldR1 = validateMovementQty(oldCt, oldPcs, { context: 'source side of send-to-broken', packetNo: sourcePacket?.packet_no });
  if (oldR1) errors.push(oldR1);
  if (oldCt > maxCt + 1e-9) errors.push(`Old carats exceed packet balance (${maxCt} ct available)`);
  if (oldPcs > maxPcs) errors.push(`Old pieces exceed packet balance (${maxPcs} pcs available)`);
  const newR1 = validateMovementQty(newCt, newPcs, { context: 'kept (post-break) side of send-to-broken' });
  if (newR1) errors.push(newR1);
  if (newCt > oldCt + 1e-9) errors.push('New carats cannot exceed old carats (broken pieces can only lose weight)');
  // Wave 3.49 Rule 2 — source's post-move state must remain consistent.
  const sourceStateErr = assertStateConsistent(maxCt - oldCt, maxPcs - oldPcs, {
    packetNo: sourcePacket?.packet_no, label: 'source packet',
    context: 'send-to-broken', txnCarats: oldCt, txnPieces: oldPcs,
  });
  if (sourceStateErr) errors.push(sourceStateErr);
  if (targetMode === 'existing' && !targetBrokenPacketId) errors.push('Pick an existing broken packet (or switch to "Create new")');
  if (srcRate <= 0) errors.push('Source packet has no rate set — cannot preserve value through break.');

  const submit = async () => {
    if (errors.length) return;
    setSubmitting(true);
    try {
      const body = {
        old_carats: oldCt,
        old_pieces: oldPcs,
        new_carats: newCt,
        new_pieces: newPcs,
        target_mode: targetMode,
        note: (form.note || '').trim(),
      };
      if (targetMode === 'existing') {
        body.target_broken_packet_id = targetBrokenPacketId;
      } else {
        body.new_broken_packet = {
          classification_id: sourcePacket.classification_id,
          description: (newBrokenDescription || '').trim() || undefined,
        };
      }
      const { data } = await api.post(
        `${cfg.apiPrefix}/packets/${sourcePacket.id}/send-to-broken`,
        body,
      );
      toast.success(
        `Sent to Broken — ${fmtCt(oldCt)} from ${sourcePacket.packet_no} → ${fmtCt(newCt)} in ${data?.broken_packet?.packet_no || 'broken packet'}`,
      );
      onOpenChange(false);
      if (onCompleted) onCompleted(data);
    } catch (e) {
      toast.error(extractError(e, 'Send-to-Broken failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!sourcePacket) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="send-to-broken-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageX size={16} className="text-rose-700" />
            Send to Broken — {sourcePacket.packet_no}
          </DialogTitle>
          <DialogDescription>
            Use this when material breaks during handling. Source packet loses the old weight at its
            current rate; the broken (salvage) packet receives the (often smaller) new weight at the
            same total value — no money is lost on the books.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2 text-xs grid grid-cols-3 gap-3" data-testid="send-source-snapshot">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-rose-700">Source available</div>
            <div className="font-mono text-rose-900">
              {fmtCt(maxCt)} · {maxPcs} pcs
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-rose-700">Source rate</div>
            <div className="font-mono text-rose-900">{fmtMoney(srcRate)}/ct</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-rose-700">Classification</div>
            <div className="text-rose-900">{sourcePacket.classification_shape || sourcePacket.classification_schema}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="rounded-md border border-[var(--divider)] p-3 bg-slate-50/40">
            <div className="text-xs font-medium text-slate-700 mb-2">Old (before break)</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-slate-500">Carats</Label>
                <Input
                  type="number" step="0.001" min="0"
                  value={form.old_carats}
                  onChange={(e) => setForm((p) => ({ ...p, old_carats: e.target.value }))}
                  className="h-9 font-mono tabular-nums"
                  data-testid="send-old-carats-input"
                />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Pieces</Label>
                <Input
                  type="number" min="0"
                  value={form.old_pieces}
                  onChange={(e) => setForm((p) => ({ ...p, old_pieces: e.target.value }))}
                  className="h-9 font-mono tabular-nums"
                  data-testid="send-old-pieces-input"
                />
              </div>
            </div>
          </div>
          <div className="rounded-md border border-amber-200 p-3 bg-amber-50/40">
            <div className="text-xs font-medium text-amber-800 mb-2 flex items-center gap-1">
              <ArrowRight size={12} /> New (in broken packet)
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-amber-700">Carats</Label>
                <Input
                  type="number" step="0.001" min="0"
                  value={form.new_carats}
                  onChange={(e) => setForm((p) => ({ ...p, new_carats: e.target.value }))}
                  className="h-9 font-mono tabular-nums"
                  data-testid="send-new-carats-input"
                />
              </div>
              <div>
                <Label className="text-[10px] text-amber-700">Pieces</Label>
                <Input
                  type="number" min="0"
                  value={form.new_pieces}
                  onChange={(e) => setForm((p) => ({ ...p, new_pieces: e.target.value }))}
                  className="h-9 font-mono tabular-nums"
                  data-testid="send-new-pieces-input"
                />
              </div>
            </div>
          </div>
        </div>

        {(oldCt > 0 && newCt > 0) && (
          <div className="rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs grid grid-cols-3 gap-3" data-testid="send-math-preview">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-blue-700">Weight reduction</div>
              <div className="font-mono text-blue-900">{fmtCt(weightLoss)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-blue-700">Value preserved</div>
              <div className="font-mono text-blue-900" data-testid="send-preserved-value">{fmtMoney(preservedValue)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-blue-700">Broken-packet rate</div>
              <div className="font-mono text-blue-900">{fmtMoney(newRate)}/ct</div>
            </div>
          </div>
        )}

        <div className="space-y-2 mt-2">
          <Label className="text-xs text-slate-700">Destination broken packet</Label>
          <Tabs value={targetMode} onValueChange={setTargetMode}>
            <TabsList className="grid grid-cols-2 w-full max-w-md">
              <TabsTrigger value="existing" disabled={!loadingTargets && brokenPackets.length === 0} data-testid="send-target-mode-existing">
                Existing broken packet
              </TabsTrigger>
              <TabsTrigger value="new" data-testid="send-target-mode-new">
                Create new broken packet
              </TabsTrigger>
            </TabsList>
            <TabsContent value="existing" className="mt-3">
              {loadingTargets ? (
                <div className="flex items-center justify-center py-6 text-xs text-slate-500">
                  <Loader2 size={14} className="animate-spin mr-2" /> Loading…
                </div>
              ) : brokenPackets.length === 0 ? (
                <div className="text-xs text-slate-500 italic text-center py-4">
                  No broken packets of this classification yet. Switch to "Create new" to spin one up.
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-[var(--divider)] rounded-md" data-testid="send-target-list">
                  {brokenPackets.map((p) => {
                    const sel = p.id === targetBrokenPacketId;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setTargetBrokenPacketId(p.id)}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors border-b border-[var(--divider)] last:border-b-0 ${sel ? 'bg-amber-50/70' : ''}`}
                        data-testid={`send-target-option-${p.id}`}
                      >
                        <div className="min-w-0">
                          <div className="font-mono font-medium text-[var(--ink-navy)] truncate">
                            {p.packet_no} <span className="text-amber-700">·BR</span>
                          </div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {fmtCt(p.balance_carats)} · {p.balance_pieces} pcs · {fmtMoney(p.balance_value)}
                          </div>
                        </div>
                        {sel && <Badge className="bg-amber-600 text-white text-[10px]">Selected</Badge>}
                      </button>
                    );
                  })}
                </div>
              )}
            </TabsContent>
            <TabsContent value="new" className="space-y-2 mt-3">
              <div className="text-xs text-slate-600 leading-relaxed">
                A new broken packet will be created automatically with classification{' '}
                <span className="font-medium text-slate-800">
                  {sourcePacket.classification_shape || sourcePacket.classification_schema}
                </span>
                {' '}and a fresh <span className="font-mono">-BR</span> packet number.
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Description (optional)</Label>
                <Input
                  value={newBrokenDescription}
                  onChange={(e) => setNewBrokenDescription(e.target.value)}
                  placeholder={`e.g. Salvage from ${sourcePacket.packet_no}`}
                  className="h-9"
                  data-testid="send-new-broken-description-input"
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-2">
          <Label className="text-[11px] text-slate-500">Note (optional)</Label>
          <Textarea
            rows={2}
            value={form.note}
            onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
            placeholder="What happened — how the piece broke, who reported it, etc."
          />
        </div>

        {errors.length > 0 && (
          <ul className="text-[11px] text-rose-600 list-disc pl-4 space-y-0.5" data-testid="send-errors">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            className="luxury-button"
            onClick={submit}
            disabled={submitting || errors.length > 0}
            data-testid="send-to-broken-submit-button"
          >
            <PackageX size={13} className="mr-1.5" />
            {submitting ? 'Posting…' : 'Send to Broken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
